import {FeatureComparisonTable} from '@primer/react-brand/esm'
import type {ComparisonTable} from '../../../schemas/contentful/contentTypes/comparisonTable'
import {documentToPlainTextString, getAnalyticsEvent} from '../../../lib/utils/analytics'
import {t} from '../../../lib/utils/localization'
import {comparisonRowValues, selectComparisonColumns, selectComparisonRowValues} from './comparison-projection'
import {hasComparisonDescription, renderComparisonRichText} from './render-comparison-rich-text'

export type ContentfulFeatureComparisonTableProps = {
  component: ComparisonTable
  view: string
}

export function validateComparisonTable(component: ComparisonTable, view: string): void {
  const columns = selectComparisonColumns(component, view)

  for (const group of component.fields.groups) {
    for (const row of group.fields.rows) {
      selectComparisonRowValues(comparisonRowValues(row.fields), columns)
    }
  }
}

const statusLabels = {
  included: 'Included',
  refresh: 'Refresh',
  unlimited: 'Unlimited',
  unavailable: 'Not included',
}

export function ContentfulFeatureComparisonTable({component, view}: ContentfulFeatureComparisonTableProps) {
  const columns = selectComparisonColumns(component, view)
  const tableHeading = component.fields.heading

  const groups = component.fields.groups.map(group => (
    <FeatureComparisonTable.Group key={group.sys.id}>
      <FeatureComparisonTable.GroupHeading id={group.fields.anchorId}>
        {group.fields.heading}
      </FeatureComparisonTable.GroupHeading>
      {group.fields.rows.map(row => {
        const richTextAnalytics = {
          analyticsContext: documentToPlainTextString(row.fields.heading),
          analyticsLocation: tableHeading,
        }

        return (
          <FeatureComparisonTable.Row key={row.sys.id}>
            <FeatureComparisonTable.RowHeading>
              {renderComparisonRichText(row.fields.heading, {inline: true, ...richTextAnalytics})}
            </FeatureComparisonTable.RowHeading>

            {hasComparisonDescription(row.fields.description) && (
              <FeatureComparisonTable.RowDescription>
                {renderComparisonRichText(row.fields.description, {inline: true, ...richTextAnalytics})}
              </FeatureComparisonTable.RowDescription>
            )}

            {selectComparisonRowValues(comparisonRowValues(row.fields), columns).map(value => {
              const variant =
                value.status === 'none'
                  ? {}
                  : {
                      variant: value.status,
                      variantAriaLabel: t(statusLabels[value.status]),
                    }
              return (
                <FeatureComparisonTable.Cell key={value.tier} {...variant}>
                  {renderComparisonRichText(value.content, {inline: false, ...richTextAnalytics})}
                </FeatureComparisonTable.Cell>
              )
            })}
          </FeatureComparisonTable.Row>
        )
      })}
    </FeatureComparisonTable.Group>
  ))

  return (
    <FeatureComparisonTable>
      <FeatureComparisonTable.Heading>{tableHeading}</FeatureComparisonTable.Heading>
      {columns.map(({fields}) => (
        <FeatureComparisonTable.Item key={fields.tierKey}>
          {fields.badge && <FeatureComparisonTable.Label>{fields.badge}</FeatureComparisonTable.Label>}
          <FeatureComparisonTable.ItemHeading>{fields.heading}</FeatureComparisonTable.ItemHeading>
          {fields.description && (
            <FeatureComparisonTable.Description>{fields.description}</FeatureComparisonTable.Description>
          )}
          {fields.displayPrice && <FeatureComparisonTable.Price>{fields.displayPrice}</FeatureComparisonTable.Price>}
          {fields.primaryAction && (
            <FeatureComparisonTable.PrimaryAction
              as="a"
              href={fields.primaryAction.fields.href}
              target={fields.primaryAction.fields.openInNewTab ? '_blank' : undefined}
              rel={fields.primaryAction.fields.openInNewTab ? 'noopener noreferrer' : undefined}
              {...getAnalyticsEvent(
                {
                  action: fields.primaryAction.fields.text,
                  tag: 'button',
                  context: 'CTAs',
                  location: fields.heading,
                },
                {context: false},
              )}
            >
              {fields.primaryAction.fields.text}
            </FeatureComparisonTable.PrimaryAction>
          )}
          {fields.secondaryAction && (
            <FeatureComparisonTable.SecondaryAction
              as="a"
              href={fields.secondaryAction.fields.href}
              target={fields.secondaryAction.fields.openInNewTab ? '_blank' : undefined}
              rel={fields.secondaryAction.fields.openInNewTab ? 'noopener noreferrer' : undefined}
              {...getAnalyticsEvent(
                {
                  action: fields.secondaryAction.fields.text,
                  tag: 'button',
                  context: 'CTAs',
                  location: fields.heading,
                },
                {context: false},
              )}
            >
              {fields.secondaryAction.fields.text}
            </FeatureComparisonTable.SecondaryAction>
          )}
        </FeatureComparisonTable.Item>
      ))}
      {groups}
    </FeatureComparisonTable>
  )
}

ContentfulFeatureComparisonTable.displayName = 'ContentfulFeatureComparisonTable'
